/**
 * Main App Component - Routing & Layout
 * ThePedigreeApp - COMPLETE VERSION
 * 
 * Includes: Admin, Customers, Vendors, all breeder modules
 */

import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { Navbar, Footer } from './components/shared/Navigation';
import { LoadingScreen } from './components/shared/UIComponents';

// Pages
import HomePage from './pages/HomePage';
import MarketplacePage from './pages/MarketplacePage';
import { LoginPage, RegisterPage, VerificationPendingPage } from './pages/AuthPages';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import CommunityPage from './pages/CommunityPage';
import CatDetailPage from './pages/CatDetailPage';

// Breeder Components
import BreederDashboard from './components/breeder/BreederDashboard';
import CommissionLedger from './components/breeder/CommissionLedger';
import RegistrationManager from './components/breeder/RegistrationManager';
import CatForm from './components/breeder/CatForm';
import BreedingProgram from './components/breeder/BreedingProgram';
import FixedAssets from './components/breeder/FixedAssets';
import ExpenseManager from './components/breeder/ExpenseManager';
import HealthRecords from './components/breeder/HealthRecords';
import LitterManager from './components/breeder/LitterManager';
import SalesManager from './components/breeder/SalesManager';
import BreederEnquiries from './components/breeder/BreederEnquiries';
import CatterySetup from './components/breeder/CatterySetup';
import CatterySettings from './components/breeder/CatterySettings';
import FoodAffiliateProgram from './components/breeder/FoodAffiliateProgram';
import GeneticsPredictor from './components/breeder/GeneticsPredictor';
import CustomerManager from './components/breeder/CustomerManager';
import VendorManager from './components/breeder/VendorManager';

// Buyer Components
import BuyerDashboard from './components/buyer/BuyerDashboard';

// Admin Components
import AdminDashboard from './components/admin/AdminDashboard';

const ProtectedRoute = ({ children, requireBreeder = false, requireAdmin = false }) => {
  const { user, loading, isAuthenticated, isBreeder, isAdmin } = useAuth();
  if (loading) return <LoadingScreen message="Loading..." />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (requireBreeder && !isBreeder) return <Navigate to="/marketplace" replace />;
  if (requireAdmin && !isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
};

const PublicLayout = () => (
  <div className="min-h-screen flex flex-col">
    <Navbar />
    <main className="flex-1"><Outlet /></main>
    <Footer />
  </div>
);

const BreederPortalLayout = () => (
  <div className="min-h-screen flex flex-col">
    <Navbar />
    <main className="flex-1"><Outlet /></main>
    <Footer />
  </div>
);

const BreederSubPageLayout = () => (
  <div className="min-h-screen flex flex-col">
    <Navbar />
    <main className="flex-1 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"><Outlet /></div>
    </main>
    <Footer />
  </div>
);

const AdminLayout = () => (
  <div className="min-h-screen flex flex-col">
    <Navbar />
    <main className="flex-1"><Outlet /></main>
  </div>
);

const PlaceholderPage = ({ title, description }) => (
  <div className="max-w-4xl mx-auto py-12 text-center">
    <h1 className="text-2xl font-display font-bold text-gray-900 mb-4">{title}</h1>
    <p className="text-gray-500">{description}</p>
  </div>
);

const BreedsPage = () => <PlaceholderPage title="Breed Directory" description="Browse all cat breeds available in South Africa" />;
const MyCatsPage = () => <PlaceholderPage title="My Cats" description="Manage your cat listings" />;

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* PUBLIC PAGES */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/cat/:id" element={<CatDetailPage />} />
            <Route path="/breeds" element={<BreedsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/community" element={<CommunityPage />} />
          </Route>

          {/* AUTH PAGES */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verification-pending" element={<VerificationPendingPage />} />

          {/* BUYER DASHBOARD */}
          <Route path="/my-account" element={<ProtectedRoute><BreederSubPageLayout /></ProtectedRoute>}>
            <Route index element={<BuyerDashboard />} />
          </Route>

          {/* CATTERY SETUP */}
          <Route path="/cattery/setup" element={<ProtectedRoute requireBreeder><CatterySetup /></ProtectedRoute>} />

          {/* BREEDER DASHBOARD - FULL PAGE */}
          <Route path="/dashboard" element={<ProtectedRoute requireBreeder><BreederPortalLayout /></ProtectedRoute>}>
            <Route index element={<BreederDashboard />} />
          </Route>

          {/* BREEDER SUB-PAGES */}
          <Route path="/dashboard" element={<ProtectedRoute requireBreeder><BreederSubPageLayout /></ProtectedRoute>}>
            <Route path="cats" element={<MyCatsPage />} />
            <Route path="cats/new" element={<CatForm />} />
            <Route path="cats/:id/edit" element={<CatForm />} />
            <Route path="enquiries" element={<BreederEnquiries />} />
            <Route path="registrations" element={<RegistrationManager />} />
            <Route path="ledger" element={<CommissionLedger />} />
            <Route path="breeding" element={<BreedingProgram />} />
            <Route path="litters" element={<LitterManager />} />
            <Route path="sales" element={<SalesManager />} />
            <Route path="customers" element={<CustomerManager />} />
            <Route path="vendors" element={<VendorManager />} />
            <Route path="assets" element={<FixedAssets />} />
            <Route path="finances" element={<ExpenseManager />} />
            <Route path="health" element={<HealthRecords />} />
            <Route path="food-partners" element={<FoodAffiliateProgram />} />
            <Route path="genetics" element={<GeneticsPredictor />} />
            <Route path="settings" element={<CatterySettings />} />
          </Route>

          {/* ADMIN DASHBOARD */}
          <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
